import { useEffect, useRef, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { rules, STATUS_KEY } from "@/lib/wcpEngine";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ClipboardCopy, Loader2, Sparkles } from "lucide-react";

const LANGS = ["English", "Spanish", "Polish", "Ukrainian", "Russian", "Arabic", "Vietnamese", "Other"];
const STATES = ["Illinois", "Wisconsin", "Other"];
const DMSES = ["Dealertrack", "CDK", "Reynolds", "Tekion", "Other"];

const STATUS_LIGHT = {
  ready_to_submit: { cls: "bg-[#e8f6ef] text-[#16794b] border-[#b7e2cd]", label: "Ready to Submit" },
  pending_manual: { cls: "bg-[#eef3fd] text-[#1f376c] border-[#cddaf5]", label: "Ready — Pending Manual Verification" },
  review: { cls: "bg-[#fff4d6] text-[#9a6700] border-[#f0dca0]", label: "Review Before Submission" },
  required: { cls: "bg-[#fdecea] text-[#b42318] border-[#f3c9c4]", label: "Required Before Submission" },
  skip_maintenance: { cls: "bg-slate-100 text-slate-600 border-slate-300", label: "Skip — Maintenance" },
};
const scoreLight = (s) => (s == null ? "text-slate-400" : s >= 90 ? "text-[#16794b]" : s >= 70 ? "text-[#2457d6]" : s >= 50 ? "text-[#9a6700]" : "text-[#b42318]");
const elapsed = (a, b) => {
  if (!a || !b) return null;
  const [ah, am] = a.split(":").map(Number), [bh, bm] = b.split(":").map(Number);
  let x = ah * 60 + am, y = bh * 60 + bm; if (y < x) y += 1440;
  return Math.round(((y - x) / 60) * 10) / 10;
};

// 23-NA-154 free-text strictness
const isSpecific = (t) => /(stuck|binding|leak(?:ing)?\s+(?:from|at)|open circuit|short(?:ed)?|excessive play|excessive metal|cracked|broken|seized|out of spec|below spec|above spec|no voltage|no ground|failed internally|worn|damaged|no communication|(?:p|u|b|c)\d{4})/i.test(t || "");
const isProcedureOnly = (t) => /^(pressure tested|scanned|inspected|checked|tested|performed|verified)\b/i.test((t || "").trim()) && !isSpecific(t);
const vagueCause = (t) => { t = (t || "").trim(); return !t || isProcedureOnly(t) || (/\binoperative\b/i.test(t) && !isSpecific(t)) || (t.length < 22 && !isSpecific(t)); };
const vagueCorrection = (t) => { t = (t || "").trim(); return !t || /^(replaced engine|turned rotors|replaced|repair(?:ed)?|fixed|all ok)\.?$/i.test(t) || t.length < 20; };

const Card = ({ children, className = "" }) => (
  <div className={`mb-4 rounded-2xl border border-[#dfe5ef] bg-white p-5 shadow-[0_2px_10px_rgba(20,30,55,0.04)] ${className}`}>{children}</div>
);
const SectionTitle = ({ children, badge }) => (
  <div className="mb-3 flex items-center justify-between gap-3">
    <h3 className="text-base font-bold text-[#172033]">{children}</h3>
    {badge && <span className="rounded-full bg-[#edf1f7] px-2.5 py-1 text-xs font-medium text-[#40506e]">{badge}</span>}
  </div>
);
const Lbl = ({ children, req, className = "" }) => (
  <label className={`mb-1.5 block text-sm font-bold text-[#172033] ${className}`}>{children}{req && <span className="ml-1 font-normal text-[#b42318]">— required</span>}</label>
);
const inputCls = "w-full rounded-lg border border-[#cfd7e5] bg-white px-3 py-2.5 text-sm text-[#172033] placeholder:text-slate-400 focus:border-[#2457d6] focus:outline-none focus:ring-2 focus:ring-[#2457d6]/20";
const Check = ({ id, checked, onChange, children }) => (
  <label className="flex items-start gap-2 text-sm text-[#172033]"><input type="checkbox" data-testid={id} className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} />{children}</label>
);

export default function TechnicianAssist() {
  const { user } = useAuth();
  const [f, setF] = useState({
    ro: "", line: "",
    dealerState: STATES.includes(user?.dealership?.state) ? user.dealership.state : "Illinois",
    dms: DMSES.includes(user?.dealership?.dms) ? user.dealership.dms : "Dealertrack",
    laborOps: "", language: "English",
    concern: "", addon: false, addonHow: "", addonSm: false,
    diagPath: "", cause: "", dtc: "", reading: "",
    correction: "",
    diagYes: false, olhYes: false, diagAmt: "",
    diagPunches: [{ start: "", end: "" }], repairPunches: [{ start: "", end: "" }], olhPunches: [{ start: "", end: "" }],
    olhOp: "", olhAmt: "", olhWhy: "", smApproval: false,
    ilBase: "", ilMult: "", ilAdd: "", mileageOut: false,
    spsCode: "", acCode: "", batteryCode: "", oneTime: "",
    removedSerial: "", installedSerial: "",
  });
  const [result, setResult] = useState(null);
  const [story, setStory] = useState("");
  const [confirmEng, setConfirmEng] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [building, setBuilding] = useState(false);
  const [savedAt, setSavedAt] = useState("");
  const saveRef = useRef();

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const setBool = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const addPunch = (k) => () => setF((p) => ({ ...p, [k]: [...p[k], { start: "", end: "" }] }));
  const rmPunch = (k, i) => () => setF((p) => ({ ...p, [k]: p[k].filter((_, idx) => idx !== i) }));
  const setPunch = (k, i, field) => (e) => setF((p) => ({ ...p, [k]: p[k].map((row, idx) => (idx === i ? { ...row, [field]: e.target.value } : row)) }));

  useEffect(() => {
    clearTimeout(saveRef.current);
    saveRef.current = setTimeout(() => {
      try { localStorage.setItem("techportal_draft_" + (f.ro || "unassigned"), JSON.stringify(f)); setSavedAt(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })); } catch { /* ignore */ }
    }, 800);
    return () => clearTimeout(saveRef.current);
  }, [f]);

  const profile = { state: f.dealerState, dms: f.dms, oem: user?.dealership?.oem || "" };
  const dealerName = user?.dealership?.name || "Pilot";
  const diagOn = !!f.diagYes, olhOn = !!f.olhYes, isIllinois = f.dealerState === "Illinois";

  const composeStory = (causeNarr, corr, opts = {}) => {
    const { pending = false, lang = "" } = opts;
    const kv = (label, val) => (val && String(val).trim() ? `${label}: ${String(val).trim()}` : null);
    const tag = pending ? `[ENGLISH TRANSLATION REQUIRED FROM ${lang.toUpperCase()}]\n` : "";
    const parts = [];
    const head = [kv("RO", f.ro), kv("Line", f.line), kv("Labor Op(s)", f.laborOps)].filter(Boolean).join("    ");
    if (head) parts.push(head);
    parts.push("CONCERN (COPIED FROM DMS):\n" + (f.concern || "").trim() + (f.addon && f.addonHow.trim() ? `\nADD-ON — identified: ${f.addonHow.trim()}` : ""));
    const causeLines = [
      (tag + (causeNarr || "").trim()).trim(),
      kv("DTC(s)", f.dtc),
      kv("Reading / spec", f.reading),
      kv("Diagnostic steps", f.diagPath),
    ].filter((x) => x && x.trim());
    parts.push("CAUSE:\n" + causeLines.join("\n"));
    parts.push("CORRECTION:\n" + (tag + (corr || "").trim()).trim());
    const punchStr = (arr) => arr.filter((r) => r.start && r.end).map((r) => `${r.start}-${r.end}`).join(", ");
    const labor = [
      diagOn ? `Diagnosis: ${f.diagAmt || "?"} hr${punchStr(f.diagPunches) ? ` (punch ${punchStr(f.diagPunches)})` : ""}` : null,
      punchStr(f.repairPunches) ? `Repair punch: ${punchStr(f.repairPunches)}` : null,
      olhOn ? `Other Labor Hours: ${f.olhOp || "?"} ${f.olhAmt || "?"} hr${punchStr(f.olhPunches) ? ` (punch ${punchStr(f.olhPunches)})` : ""}${f.olhWhy ? ` - ${f.olhWhy}` : ""}${f.smApproval ? "; Service Management approval documented" : ""}` : null,
      (isIllinois && (f.ilBase || f.ilMult || f.ilAdd)) ? `Illinois labor-time: base ${f.ilBase || "-"} / multiplier ${f.ilMult || "-"} / additional ${f.ilAdd || "-"}` : null,
      (f.mileageOut === true || f.mileageOut === "yes") ? "Mileage Out entered in DMS: Yes" : null,
    ].filter(Boolean);
    if (labor.length) parts.push("LABOR TIME & PUNCHES:\n" + labor.join("\n"));
    const support = [
      kv("SPS Warranty Claim Code", f.spsCode),
      kv("A/C Warranty Code (R-1234yf)", f.acCode),
      kv("Battery test printout code", f.batteryCode),
      f.oneTime ? `One-time-use parts / fasteners per SI: ${f.oneTime === "yes" ? "Yes" : "No"}` : null,
      kv("Removed component serial", f.removedSerial),
      kv("Installed component serial", f.installedSerial),
    ].filter(Boolean);
    if (support.length) parts.push("SUPPORTING RECORDS:\n" + support.join("\n"));
    return parts.join("\n\n");
  };

  const runCheck = () => {
    const punches = [];
    for (const arr of [f.diagPunches, f.repairPunches, f.olhPunches]) for (const r of arr) { const h = elapsed(r.start, r.end); if (h != null) punches.push({ start: r.start, end: r.end, hours: h }); }
    const full = (arr) => arr.some((r) => r.start && r.end);
    const causeText = (f.cause || "").trim();
    const causeForEngine = [causeText, f.dtc, f.reading].filter(Boolean).join(". ");
    const opText = [f.laborOps, f.cause, f.correction, f.olhOp].join(" ");
    const ops = [...new Set(opText.match(/\b\d{7}\b/g) || [])];
    const norm = [
      f.concern, f.addon ? "ADD-ON " + f.addonHow : "", f.diagPath, causeText, f.dtc, f.reading,
      f.correction, f.laborOps, diagOn ? "Diagnosis: " + f.diagAmt : "", olhOn ? "OLH " + f.olhOp + " " + f.olhWhy : "",
      f.spsCode ? "SPS " + f.spsCode : "", f.acCode ? "A/C " + f.acCode : "", f.batteryCode ? "battery test " + f.batteryCode : "",
      f.removedSerial ? "removed serial " + f.removedSerial : "", f.installedSerial ? "installed serial " + f.installedSerial : "",
    ].join(" ");
    const p = {
      lines: norm.split("\n"), norm, vin: "", ro: f.ro, ops,
      concern: f.concern, cause: causeForEngine, correction: f.correction, parts: [],
      punches, diagnosis: diagOn ? parseFloat(f.diagAmt) || 0 : 0, claimedTotal: 0, firstPunchMatch: false,
    };
    const r = rules(p, profile, { suppressRules: ["RULE_VIN_PRESENT", "RULE_JOBCARD_PRESENT", "RULE_COMPLAINT_PRESENT", "RULE_COMPLAINT_NOT_VAGUE"] });

    const programming = /program|\bsps\b|calibrat/i.test(norm);
    const ac1234yf = /\b1234yf\b/i.test(norm) || (/\b(?:a\/?c|air conditioning|refrigerant)\b/i.test(norm) && /evacuat|recharge|recover/i.test(norm));
    const batteryRepl = /\bbatter(?:y|ies)\b/i.test(norm) && /replac|install|renew|exchang/i.test(norm);

    const extra = [];
    if (!f.laborOps.trim()) extra.push("LABOR OPERATION(S): required — enter the applicable labor operation(s).");
    const evidence = [f.dtc, f.reading].some((x) => x.trim());
    if (vagueCause(causeText) && !evidence) extra.push("CAUSE / FINDINGS: identify the actual failure and how it was verified (failed component, diagnostic reading, or DTC).");
    if (vagueCorrection(f.correction)) extra.push("CORRECTION: document the detailed repair steps actually performed.");
    if (f.addon) {
      if (!f.addonHow.trim()) extra.push("ADD-ON: document how the additional condition was identified / communicated.");
      if (!f.addonSm) extra.push("ADD-ON: Service Management approval must be documented.");
    }
    if (diagOn) { if (!f.diagAmt.trim()) extra.push("DIAGNOSIS TIME: amount required."); if (!full(f.diagPunches)) extra.push("DIAGNOSIS PUNCH: at least one start/end punch required."); }
    if (olhOn) {
      if (!f.olhOp.trim()) extra.push("OTHER LABOR HOURS: labor operation required.");
      if (!f.olhAmt.trim()) extra.push("OTHER LABOR HOURS: claimed amount required.");
      if (f.olhWhy.trim().length < 20) extra.push("OTHER LABOR HOURS: provide an explanation of the OLH request.");
      if (!f.smApproval) extra.push("OTHER LABOR HOURS: Service Management approval must be documented.");
      if (!full(f.olhPunches)) extra.push("OTHER LABOR HOURS PUNCH: at least one distinguishable start/end punch required.");
    }
    if (isIllinois && (diagOn || olhOn) && !(f.ilBase.trim() || f.ilMult.trim() || f.ilAdd.trim())) extra.push("ILLINOIS LABOR-TIME REVIEW: enter base / multiplier / additional time as applicable.");
    if (f.mileageOut !== true && f.mileageOut !== "yes") extra.push("MILEAGE OUT: enter / verify Mileage Out in the DMS before completing the repair order.");
    if (programming && !f.spsCode.trim()) extra.push("SPS WARRANTY CLAIM CODE: required for programming / SPS.");
    if (ac1234yf && !f.acCode.trim()) extra.push("A/C WARRANTY CODE: required for R-1234yf evacuation / recharge.");
    if (batteryRepl && !f.batteryCode.trim()) extra.push("BATTERY TEST PRINTOUT: enter the code from the battery test printout attached to the RO.");
    if (f.oneTime !== "yes") extra.push("ONE-TIME-USE PARTS: must be answered Yes — confirm all required one-time-use parts / fasteners per SI.");

    const hard = r.findings.filter((x) => x.severity === "critical" || x.severity === "high");
    const med = r.findings.filter((x) => x.severity === "medium");
    const requiredCount = hard.length + extra.length;
    let status, statusLabel, score;
    if (requiredCount > 0) { status = "required"; statusLabel = "REQUIRED BEFORE SUBMISSION"; score = Math.max(0, Math.min(r.score, 100 - requiredCount * 15)); }
    else if (med.length > 0) { status = "review"; statusLabel = "REVIEW BEFORE SUBMISSION"; score = Math.min(r.score, 70); }
    else { status = STATUS_KEY[r.status] || "pending_manual"; statusLabel = r.status; score = r.score; }

    const out = { findings: r.findings, packs: r.packs, status, statusLabel, score, extra };
    setResult(out);
    api.post("/technician/usage", {
      ro_number: f.ro, status, status_label: statusLabel, score,
      findings_count: out.findings.length + extra.length,
    }).catch(() => {});
  };

  const translateToEnglish = async () => {
    if (f.language === "English") return toast.info("Language is already English.");
    if (!f.cause.trim() && !f.correction.trim()) return toast.error("Enter a cause and/or correction to translate.");
    setTranslating(true);
    try {
      const { data } = await api.post("/technician/translate", { language: f.language, concern: f.concern, cause: f.cause, correction: f.correction });
      setStory(composeStory(data.cause, data.correction));
      toast.success(`Translated from ${f.language} to English`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed — please try again.");
    } finally { setTranslating(false); }
  };
  const buildEnglish = () => {
    const s = f.language !== "English"
      ? composeStory(f.cause, f.correction, { pending: true, lang: f.language })
      : composeStory(f.cause, f.correction);
    setStory(s); return s;
  };
  const proofreadEnglish = async () => {
    const base = buildEnglish();
    if (!f.cause.trim() && !f.correction.trim()) return;
    setBuilding(true);
    try {
      const { data } = await api.post("/technician/translate", { language: "English", concern: f.concern, cause: f.cause, correction: f.correction });
      setStory(composeStory(data.cause, data.correction));
      toast.success("Spelling checked — English story built.");
    } catch (e) {
      setStory(base);
      toast.error(e?.response?.data?.detail || "Couldn't proofread — built the story from your text as-is.");
    } finally { setBuilding(false); }
  };
  const copyStory = async () => {
    if (!confirmEng) return toast.error("Please confirm the English version is accurate first.");
    const s = story || buildEnglish();
    try { await navigator.clipboard.writeText(s); toast.success("English DMS story copied."); }
    catch { toast.error("Clipboard blocked — select the text and copy manually."); }
  };

  const traceOn = result?.packs?.some((p) => p.id === "TRACE22576");
  const renderPunches = (key, label, tid) => (
    <div>
      <Lbl>{label}</Lbl>
      {f[key].map((row, i) => (
        <div key={i} className="mb-2 flex items-center gap-2">
          <input type="time" data-testid={`${tid}-start-${i}`} className={inputCls} value={row.start} onChange={setPunch(key, i, "start")} />
          <span className="text-[#68738a]">–</span>
          <input type="time" data-testid={`${tid}-end-${i}`} className={inputCls} value={row.end} onChange={setPunch(key, i, "end")} />
          {f[key].length > 1 && <button type="button" onClick={rmPunch(key, i)} data-testid={`${tid}-remove-${i}`} className="shrink-0 rounded-md px-2 py-2 text-xs font-bold text-[#b42318] hover:bg-[#fdecea]">Remove</button>}
        </div>
      ))}
      <button type="button" onClick={addPunch(key)} data-testid={`${tid}-add`} className="mt-1 rounded-md bg-[#e9eef9] px-3 py-1.5 text-xs font-bold text-[#1f376c] hover:bg-[#dbe4f7]">+ Add another punch</button>
    </div>
  );
  const showTrace = traceOn || /\b(transmission|engine|torque convert|valve body|airbag|battery|drive unit|RESS)\b.*(replac|install|remov)/i.test([f.cause, f.correction].join(" "));
  const st = result ? (STATUS_LIGHT[result.status] || STATUS_LIGHT.pending_manual) : null;
  const totalIssues = result ? result.findings.length + result.extra.length : 0;

  return (
    <AppLayout>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-3.5rem)] bg-[#f5f7fb] sm:-mx-6 lg:-mx-8" data-testid="technician-portal">
        <div className="flex items-center justify-between bg-[#111c34] px-6 py-4 text-white">
          <div>
            <h1 className="text-xl font-bold">Warranty Compliance Pro</h1>
            <p className="mt-0.5 text-xs text-white/70">Technician Portal • Controlled Pilot</p>
          </div>
          <div className="text-sm">{user?.name || "Technician"}</div>
        </div>

        <div className="mx-auto max-w-[1160px] px-4 py-6 sm:px-6">
          {/* Intro / PRIVATE PILOT */}
          <Card>
            <div className="mb-2 flex items-center justify-between">
              <span className="rounded-full bg-[#eef3fd] px-2.5 py-1 text-xs font-bold tracking-wide text-[#1f376c]">PRIVATE PILOT</span>
              <span className="text-xs text-[#68738a]" data-testid="tech-saved">{savedAt ? `Saved ${savedAt}` : "Draft ready"}</span>
            </div>
            <p className="text-sm text-[#68738a]">Build a complete warranty story before submission. Enter only the facts you personally diagnosed and repaired. The portal will not invent DTCs, readings, failures, time, or repair steps.</p>
          </Card>

          {/* Repair Order */}
          <Card>
            <SectionTitle>Repair order</SectionTitle>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Lbl>Dealership</Lbl><input data-testid="tech-dealership" className={`${inputCls} bg-[#f7f9fd]`} value={dealerName} readOnly /></div>
              <div><Lbl>RO / Job Card <span className="text-[#b42318]" data-testid="tech-ro-required-star">★</span></Lbl><input data-testid="tech-ro" className={inputCls} placeholder="123456" value={f.ro} onChange={set("ro")} /></div>
              <div><Lbl>Repair Line</Lbl><input data-testid="tech-line" className={inputCls} placeholder="Example: Line 3" value={f.line} onChange={set("line")} /></div>
              <div><Lbl>Dealer State</Lbl><select data-testid="tech-dealer-state" className={inputCls} value={f.dealerState} onChange={set("dealerState")}>{STATES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><Lbl>DMS</Lbl><select data-testid="tech-dms" className={inputCls} value={f.dms} onChange={set("dms")}>{DMSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><Lbl req>Labor Operation(s)</Lbl><input data-testid="tech-labor-ops" className={inputCls} placeholder="4031600" value={f.laborOps} onChange={set("laborOps")} /></div>
            </div>
          </Card>

          {/* 1. Customer Concern */}
          <Card>
            <SectionTitle badge="Copy exactly from DMS">1. Customer Concern</SectionTitle>
            <Lbl className="font-normal">Copy the Customer Concern exactly from the DMS</Lbl>
            <textarea data-testid="tech-concern" className={`${inputCls} min-h-[100px] resize-y`} placeholder="customer states check engine light is on" value={f.concern} onChange={set("concern")} />
            <div className="mt-3"><Check id="tech-addon" checked={f.addon} onChange={setBool("addon")}>This is an ADD-ON discovered after the initial write-up</Check></div>
            {f.addon && (
              <div className="mt-3 space-y-3">
                <div><Lbl className="font-normal">How was the ADD-ON identified?</Lbl><textarea data-testid="tech-addon-how" className={`${inputCls} min-h-[78px] resize-y`} placeholder="Document how the condition was found / communicated." value={f.addonHow} onChange={set("addonHow")} /></div>
                <Check id="tech-addon-sm" checked={f.addonSm} onChange={setBool("addonSm")}>Service Management approval documented</Check>
              </div>
            )}
          </Card>

          {/* 2. Cause / Technician Findings */}
          <Card>
            <SectionTitle badge="Diagnosis / flow-chart assistance">2. Cause / Technician Findings</SectionTitle>
            <p className="mb-2 text-xs text-[#68738a]">Paste the diagnostic path you followed and include the actual result at each applicable step. Enter only tests you personally performed. The portal will help organize your findings — it will not invent results.</p>
            <Lbl className="font-normal">Copy your diagnosis / flow-chart steps and actual results</Lbl>
            <textarea data-testid="tech-diag-path" className={`${inputCls} min-h-[110px] resize-y`} placeholder="Example: Step 1 — Checked coolant level: full. Step 2 — Commanded thermostat test: actual result…" value={f.diagPath} onChange={set("diagPath")} />
            <div className="mt-3 space-y-4">
              <div><Lbl>Cause / failed component &amp; root cause</Lbl><textarea data-testid="tech-cause" className={`${inputCls} min-h-[90px] resize-y`} placeholder="State the failed component and the actual root cause you verified." value={f.cause} onChange={set("cause")} /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Lbl>DTC(s), if applicable</Lbl><input data-testid="tech-dtc" className={inputCls} placeholder="Example: P0128" value={f.dtc} onChange={set("dtc")} /></div>
                <div><Lbl>Actual reading and specification</Lbl><input data-testid="tech-reading" className={inputCls} value={f.reading} onChange={set("reading")} /></div>
              </div>
            </div>
          </Card>

          {/* 3. Correction */}
          <Card>
            <SectionTitle badge="Detailed repair steps performed">3. Correction</SectionTitle>
            <textarea data-testid="tech-correction" className={`${inputCls} min-h-[110px] resize-y`} placeholder="replaced evap purge sensor, cleared codes, verified repair" value={f.correction} onChange={set("correction")} />
          </Card>

          {/* 4. Labor time and punches */}
          <Card>
            <SectionTitle>4. Labor time and punches</SectionTitle>
            <div className="grid gap-2 sm:grid-cols-2">
              <Check id="tech-diag-yes" checked={f.diagYes} onChange={setBool("diagYes")}>Variable Diagnosis Time claimed</Check>
              <Check id="tech-olh-yes" checked={f.olhYes} onChange={setBool("olhYes")}>OTHER LABOR HOURS claimed</Check>
            </div>
            {diagOn && (
              <div className="mt-3 rounded-lg border-l-4 border-[#2457d6] bg-[#f7f9fd] p-3.5">
                <h4 className="mb-2 font-bold text-[#172033]">Variable Diagnosis Time</h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Lbl>Diagnosis hours</Lbl><input data-testid="tech-diag-amt" className={inputCls} placeholder="Hours" value={f.diagAmt} onChange={set("diagAmt")} /></div>
                </div>
                <div className="mt-3">{renderPunches("diagPunches", "Diagnosis punch(es)", "tech-diag")}</div>
              </div>
            )}
            <div className="mt-3">{renderPunches("repairPunches", "Repair punch(es)", "tech-repair")}</div>
            {olhOn && (
              <div className="mt-3 rounded-lg border-l-4 border-[#2457d6] bg-[#f7f9fd] p-3.5">
                <h4 className="mb-2 font-bold text-[#172033]">OTHER LABOR HOURS</h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Lbl>OLH labor operation</Lbl><input data-testid="tech-olh-op" className={inputCls} value={f.olhOp} onChange={set("olhOp")} /></div>
                  <div><Lbl>OLH amount</Lbl><input data-testid="tech-olh-amt" className={inputCls} placeholder="Hours" value={f.olhAmt} onChange={set("olhAmt")} /></div>
                  <div className="sm:col-span-2"><Lbl>Explanation of OLH request</Lbl><textarea data-testid="tech-olh-why" className={`${inputCls} min-h-[78px] resize-y`} value={f.olhWhy} onChange={set("olhWhy")} /></div>
                  <div className="sm:col-span-2"><Check id="tech-sm-approval" checked={f.smApproval} onChange={setBool("smApproval")}>Service Management approval documented</Check></div>
                  <div className="sm:col-span-2">{renderPunches("olhPunches", "OLH punch(es)", "tech-olh")}</div>
                </div>
              </div>
            )}
            {f.dealerState === "Illinois" && (
              <div className="mt-3 rounded-lg border border-[#dfe5ef] bg-[#f7f9fd] p-3.5">
                <h4 className="mb-2 font-bold text-[#172033]">Illinois labor-time review</h4>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div><Lbl>Base time</Lbl><input data-testid="tech-il-base" className={inputCls} value={f.ilBase} onChange={set("ilBase")} /></div>
                  <div><Lbl>Multiplier time</Lbl><input data-testid="tech-il-mult" className={inputCls} value={f.ilMult} onChange={set("ilMult")} /></div>
                  <div><Lbl>Additional Time</Lbl><input data-testid="tech-il-additional" className={inputCls} value={f.ilAdd} onChange={set("ilAdd")} /></div>
                </div>
              </div>
            )}
            <div className="mt-4"><Check id="tech-mileage" checked={f.mileageOut} onChange={setBool("mileageOut")}>Mileage Out entered in the DMS</Check></div>
          </Card>

          {/* Traceability */}
          {showTrace && (
            <Card className="!border-[#f3c9c4]">
              <SectionTitle badge="GM Content ID 22576">Removed Component Serial Number Traceability</SectionTitle>
              <div className="rounded-lg border-l-4 border-[#b42318] bg-[#fff7f6] p-3.5" data-testid="tech-trace-box">
                <div className="flex items-center gap-2 font-bold text-[#b42318]"><AlertTriangle className="h-4 w-4" /> SERIAL NUMBER TRACEABILITY REQUIRED</div>
                <p className="mt-1.5 text-xs text-[#68738a]">An applicable component replacement was detected. Record the actual serial numbers — do not infer them.</p>
              </div>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div><Lbl>Removed component serial number</Lbl><input data-testid="tech-removed-serial" className={inputCls} value={f.removedSerial} onChange={set("removedSerial")} /></div>
                <div><Lbl>Installed component serial number</Lbl><input data-testid="tech-installed-serial" className={inputCls} value={f.installedSerial} onChange={set("installedSerial")} /></div>
              </div>
            </Card>
          )}

          {/* 5. Supporting records */}
          <Card>
            <SectionTitle>5. Supporting records</SectionTitle>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Lbl>SPS Warranty Claim Code</Lbl><input data-testid="tech-sps-code" className={inputCls} value={f.spsCode} onChange={set("spsCode")} /></div>
              <div><Lbl>A/C Warranty Code <span className="font-normal text-[#68738a]">— required for R-1234yf evacuation/recharge</span></Lbl><input data-testid="tech-ac-code" className={inputCls} value={f.acCode} onChange={set("acCode")} /></div>
              <div><Lbl>Battery test printout attached to RO — enter code</Lbl><input data-testid="tech-battery-code" className={inputCls} placeholder="Enter code from attached battery test printout" value={f.batteryCode} onChange={set("batteryCode")} /></div>
              <div><Lbl>Do you have all required one-time-use parts / fasteners per SI?</Lbl>
                <select data-testid="tech-onetime" className={inputCls} value={f.oneTime} onChange={set("oneTime")}>
                  <option value="">Select Yes or No</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              </div>
            </div>
          </Card>

          {/* Run check + result */}
          <Card>
            <button onClick={runCheck} data-testid="tech-run-check" className="rounded-lg bg-[#2457d6] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1e49b8]">Run Compliance Check</button>
            {result && (
              <div className="mt-4" data-testid="tech-result">
                <h3 className="mb-2 text-base font-bold text-[#172033]">Compliance Result</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`rounded-full border px-3 py-1 text-xs font-bold ${st.cls}`} data-testid={`status-badge-${result.status}`}>{st.label}</span>
                  <span className={`text-2xl font-extrabold ${scoreLight(result.score)}`} data-testid="tech-score">{result.score}</span>
                </div>
                <div className={`mt-3 rounded-lg px-3 py-2.5 text-sm font-bold ${totalIssues ? "bg-[#fdecea] text-[#b42318]" : "bg-[#e8f6ef] text-[#16794b]"}`}>
                  {totalIssues ? `${totalIssues} REQUIRED ITEM(S) BEFORE DOCUMENTATION IS COMPLETE.` : "DOCUMENTATION COMPLIANT — no required deficiencies identified in the information entered."}
                </div>
                {totalIssues > 0 && (
                  <ul className="mt-3 space-y-1.5" data-testid="tech-findings">
                    {result.findings.map((x, i) => (
                      <li key={"f" + i} className="rounded-md border border-[#f3c9c4] bg-[#fdf2f0] px-3 py-2 text-xs text-[#7a231a]"><div className="font-semibold">{x.title}</div>{x.action && <div className="mt-0.5 text-[11px] text-[#9a4a41]">{x.action}</div>}</li>
                    ))}
                    {result.extra.map((x, i) => (<li key={"e" + i} className="rounded-md border border-[#f3c9c4] bg-[#fdf2f0] px-3 py-2 text-xs text-[#7a231a]">{x}</li>))}
                  </ul>
                )}
                {totalIssues === 0 && <p className="mt-3 flex items-center gap-2 text-sm text-[#16794b]"><CheckCircle2 className="h-4 w-4" /> No deficiencies found in what you entered.</p>}
                {result.packs?.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{result.packs.map((p, i) => <span key={i} className="rounded border border-[#cddaf5] bg-[#eef3fd] px-2 py-0.5 text-[10px] font-medium text-[#1f376c]">{p.label}</span>)}</div>}
              </div>
            )}
          </Card>

          {/* Final English DMS Story */}
          <Card>
            <SectionTitle badge="Copy back to DMS">Final English DMS Story</SectionTitle>
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <Lbl className="mb-0 font-normal">Preferred / native language</Lbl>
              <select data-testid="tech-language" className={`${inputCls} max-w-[220px]`} value={f.language} onChange={set("language")}>{LANGS.map((l) => <option key={l} value={l}>{l}</option>)}</select>
            </div>
            <p className="mb-2 text-xs text-[#68738a]">The Customer Concern stays exactly as copied from the DMS. Cause and Correction may be written in your preferred/native language; Technician Assist translates only those into English while preserving technical facts.</p>
            <textarea data-testid="tech-story" className={`${inputCls} min-h-[200px] resize-y`} placeholder="Final English warranty documentation will appear here after compliance review." value={story} onChange={(e) => setStory(e.target.value)} />
            <div className="mt-2"><Check id="tech-confirm-english" checked={confirmEng} onChange={setConfirmEng}>I confirm the English version accurately describes my diagnosis and repair.</Check></div>
            <div className="mt-3 flex flex-wrap gap-2">
              {f.language !== "English" && (
                <button onClick={translateToEnglish} disabled={translating} data-testid="tech-translate-english" className="inline-flex items-center gap-1.5 rounded-lg bg-[#2457d6] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1e49b8] disabled:opacity-60">
                  {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{translating ? "Translating…" : `Translate from ${f.language}`}
                </button>
              )}
              <button onClick={f.language === "English" ? proofreadEnglish : buildEnglish} disabled={building} data-testid="tech-build-english" className="inline-flex items-center gap-1.5 rounded-lg bg-[#e9eef9] px-4 py-2.5 text-sm font-bold text-[#1f376c] transition-colors hover:bg-[#dbe4f7] disabled:opacity-60">{building && <Loader2 className="h-4 w-4 animate-spin" />}{building ? "Checking spelling…" : "Build English DMS Story"}</button>
              <button onClick={copyStory} data-testid="tech-copy-english" className="inline-flex items-center gap-1.5 rounded-lg bg-[#2457d6] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1e49b8]"><ClipboardCopy className="h-4 w-4" /> Copy English Story to DMS</button>
            </div>
          </Card>

          <p className="mt-2 text-center text-xs text-[#68738a]">Controlled pilot. Compliance guidance does not guarantee claim payment. Final submission remains the responsibility of the Warranty Administrator.</p>
        </div>
      </div>
    </AppLayout>
  );
}
